import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { getData, postData, uploadFile } from '../../../webService/webservice';
import { toast } from 'react-toastify';
import { useStore } from '../../../Context/context.jsx';
import Footer from '../Footer/Footer-page';
import './CampaignWizard.css';

const STEPS = ['Campaign Details', 'Template & Channel', 'Audience Segmentation', 'Schedule & Launch'];

const CampaignWizard = () => {
  const navigate = useNavigate();
  const { campaignId } = useParams();
  const { selectedStoreId } = useStore();
  const [currentStep, setCurrentStep] = useState(0);

  // Derive Active Store ID
  const adminStoreIds = JSON.parse(sessionStorage.getItem('storeIds') || localStorage.getItem('storeIds') || '[]');
  const activeStoreId =
    selectedStoreId ||
    sessionStorage.getItem('storeId') ||
    localStorage.getItem('storeId') ||
    (adminStoreIds.length > 0 ? adminStoreIds[0] : '');

  // Form State
  const [campaignName, setCampaignName] = useState('');
  const [objective, setObjective] = useState('');
  const [campaignType, setCampaignType] = useState('WhatsApp');

  // Template State
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [messageTemplate, setMessageTemplate] = useState('');
  const [enableMediaAttachment, setEnableMediaAttachment] = useState(false);

  // Audience Type Preset: ALL | MEMBERS | VIP | CUSTOM
  const [audienceType, setAudienceType] = useState('ALL');

  // Custom Audience Filter Criteria State
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [membershipStatus, setMembershipStatus] = useState('ALL');
  const [subscriberStatus, setSubscriberStatus] = useState('ALL');
  const [gender, setGender] = useState('ALL');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [minTotalSpend, setMinTotalSpend] = useState('');
  const [maxTotalSpend, setMaxTotalSpend] = useState('');
  const [debouncedMinSpend, setDebouncedMinSpend] = useState('');
  const [debouncedMaxSpend, setDebouncedMaxSpend] = useState('');
  const [minLoyaltyPoints, setMinLoyaltyPoints] = useState('');
  const [maxLoyaltyPoints, setMaxLoyaltyPoints] = useState('');
  const [birthdayMonth, setBirthdayMonth] = useState('');
  const [anniversaryMonth, setAnniversaryMonth] = useState('');
  const [lastActiveDays, setLastActiveDays] = useState('');
  const [customerStatus, setCustomerStatus] = useState('ALL');

  // Audience Preview & Selection State
  const [previewLoading, setPreviewLoading] = useState(false);
  const [sampleCustomers, setSampleCustomers] = useState([]);
  const [selectedCustomerIds, setSelectedCustomerIds] = useState([]);
  const [audiencePage, setAudiencePage] = useState(1);
  const [audienceLimit, setAudienceLimit] = useState(25);
  const [audienceTotalCount, setAudienceTotalCount] = useState(0);
  const [audienceTotalPages, setAudienceTotalPages] = useState(1);

  // Schedule State (Immediate | Scheduled)
  const [scheduleType, setScheduleType] = useState('Immediate');
  const [scheduledAt, setScheduledAt] = useState('');
  const [launching, setLaunching] = useState(false);

  // Media Attachments State
  const [mediaLink, setMediaLink] = useState('');
  const [mediaFiles, setMediaFiles] = useState({ image: null, pdf: null, video: null });
  const [mediaUploading, setMediaUploading] = useState(false);
  const [uploadedAssets, setUploadedAssets] = useState([]);
  const imageInputRef = useRef(null);
  const pdfInputRef = useRef(null);
  const videoInputRef = useRef(null);

  // Derive if selected template requires / contains media (Image, Document, PDF, Video)
  const selectedTemplateObj = templates.find(t => t._id === selectedTemplateId);
  const selectedHeaderType = (selectedTemplateObj?.headerType || selectedTemplateObj?.mediaType || '').toUpperCase();
  const isTemplateMediaRequired = ['IMAGE', 'DOCUMENT', 'PDF', 'VIDEO'].includes(selectedHeaderType) || Boolean(selectedTemplateObj?.headerUrl || selectedTemplateObj?.mediaUrl);
  const shouldDisplayMediaSection = isTemplateMediaRequired || enableMediaAttachment;

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery), 450);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Debounce min/max spend
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedMinSpend(minTotalSpend), 500);
    return () => clearTimeout(timer);
  }, [minTotalSpend]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedMaxSpend(maxTotalSpend), 500);
    return () => clearTimeout(timer);
  }, [maxTotalSpend]);

  // Load Existing Campaign Details if editing / navigating via /sendcampaigns/:campaignId
  useEffect(() => {
    const fetchCampaignDetails = async () => {
      if (!campaignId) return;
      try {
        const token = sessionStorage.getItem('Token') || localStorage.getItem('Token');
        const res = await getData(`campaigns/${campaignId}`, {}, token);
        if ((res?.status === 200 || res?.success || res?.data) && res?.data) {
          const camp = res.data;
          setCampaignName(camp.campaignName || '');
          setObjective(camp.objective || '');
          setCampaignType(camp.campaignType || 'WhatsApp');
          setSelectedTemplateId(camp.templateId?._id || camp.templateId || '');
          setMessageTemplate(camp.messageTemplate || '');
          setScheduleType(camp.scheduleType === 'Scheduled' ? 'Scheduled' : 'Immediate');
          if (camp.scheduledAt) setScheduledAt(camp.scheduledAt.substring(0, 16));

          const savedIds = camp.manualCustomerIds || camp.audienceFilters?.selectedCustomerIds || [];
          if (Array.isArray(savedIds) && savedIds.length > 0) {
            setSelectedCustomerIds(savedIds);
          }
          if (camp.campaignAssets && camp.campaignAssets.length > 0) {
            setUploadedAssets(camp.campaignAssets);
            setEnableMediaAttachment(true);
          }
          setCurrentStep(2);
        }
      } catch (err) {
        console.error('Error fetching campaign details:', err);
      }
    };
    fetchCampaignDetails();
  }, [campaignId]);

  // Fetch Approved Meta Templates
  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const token = sessionStorage.getItem('Token') || localStorage.getItem('Token');
        const res = await getData('templates', { channel: 'WhatsApp', onlyApproved: 'true', limit: 100 }, token);
        if ((res?.status === 200 || res?.success || res?.data) && res?.data?.templates) {
          const approvedList = res.data.templates.filter(t => (t.metaStatus || 'DRAFT').toUpperCase() === 'APPROVED' && t.isActive !== false);
          setTemplates(approvedList.length > 0 ? approvedList : res.data.templates);
        }
      } catch (err) {
        console.error('Error fetching templates:', err);
      }
    };
    fetchTemplates();
  }, [campaignType]);

  // Fetch Audience Preview based on filters and pagination
  const handlePreviewAudience = async (forceReset = false, pageNum = audiencePage) => {
    setPreviewLoading(true);
    try {
      const token = sessionStorage.getItem('Token') || localStorage.getItem('Token');
      const payload = {
        storeId: activeStoreId,
        page: pageNum,
        limit: audienceLimit,
        filters: {
          search: debouncedSearch,
          membershipStatus,
          subscriberStatus,
          gender,
          city,
          state,
          minTotalSpend: debouncedMinSpend,
          maxTotalSpend: debouncedMaxSpend,
          minLoyaltyPoints,
          maxLoyaltyPoints,
          birthdayMonth,
          anniversaryMonth,
          lastActiveDays,
          status: customerStatus
        }
      };

      const res = await postData('campaigns/preview-audience', payload, token);
      if ((res?.status === 200 || res?.success || res?.data) && res?.data) {
        const fetchedCustomers = res.data.sampleCustomers || [];
        setSampleCustomers(fetchedCustomers);

        const totalCount = res.data.totalCount !== undefined ? res.data.totalCount : res.data.recipientCount || fetchedCustomers.length;
        setAudienceTotalCount(totalCount);
        setAudienceTotalPages(res.data.totalPages || Math.ceil((totalCount || 1) / audienceLimit) || 1);

        const pageIds = fetchedCustomers.map(c => c._id).filter(Boolean);

        if (forceReset || selectedCustomerIds.length === 0) {
          setSelectedCustomerIds(pageIds);
        }
      }
    } catch (err) {
      console.error('Audience preview error:', err);
      toast.error('Failed to preview audience');
    } finally {
      setPreviewLoading(false);
    }
  };

  // Re-trigger preview whenever filters or page changes
  useEffect(() => {
    if (currentStep === 2) {
      handlePreviewAudience(false, audiencePage);
    }
  }, [
    currentStep,
    audiencePage,
    audienceLimit,
    debouncedSearch,
    membershipStatus,
    subscriberStatus,
    gender,
    city,
    state,
    debouncedMinSpend,
    debouncedMaxSpend,
    minLoyaltyPoints,
    maxLoyaltyPoints,
    birthdayMonth,
    anniversaryMonth,
    lastActiveDays,
    customerStatus,
    activeStoreId
  ]);

  // Handle Preset Audience Type Selection
  const handleSelectAudienceType = (type) => {
    setAudienceType(type);
    setAudiencePage(1);

    if (type === 'ALL') {
      setMembershipStatus('ALL');
      setSubscriberStatus('ALL');
      setGender('ALL');
      setMinTotalSpend('');
      setMaxTotalSpend('');
      setCity('');
      setSearchQuery('');
      setLastActiveDays('');
      setBirthdayMonth('');
    } else if (type === 'MEMBERS') {
      setMembershipStatus('Member');
      setSubscriberStatus('Subscribers');
      setGender('ALL');
      setMinTotalSpend('');
    } else if (type === 'VIP') {
      setMembershipStatus('ALL');
      setSubscriberStatus('ALL');
      setMinTotalSpend('5000');
    }
    setTimeout(() => handlePreviewAudience(true, 1), 50);
  };

  // Clear all custom filters
  const handleResetFilters = () => {
    setSearchQuery('');
    setMembershipStatus('ALL');
    setSubscriberStatus('ALL');
    setGender('ALL');
    setCity('');
    setState('');
    setMinTotalSpend('');
    setMaxTotalSpend('');
    setMinLoyaltyPoints('');
    setMaxLoyaltyPoints('');
    setBirthdayMonth('');
    setAnniversaryMonth('');
    setLastActiveDays('');
    setCustomerStatus('ALL');
    setAudiencePage(1);
    handlePreviewAudience(true, 1);
  };

  // Checkbox helpers
  const isPageAllSelected = sampleCustomers.length > 0 && sampleCustomers.every(c => selectedCustomerIds.includes(c._id));
  const isPageSomeSelected = sampleCustomers.some(c => selectedCustomerIds.includes(c._id));

  const handleToggleSelectAllPage = (e) => {
    if (e.target.checked) {
      const pageIds = sampleCustomers.map(c => c._id).filter(Boolean);
      setSelectedCustomerIds(prev => Array.from(new Set([...prev, ...pageIds])));
    } else {
      const pageIdsSet = new Set(sampleCustomers.map(c => c._id));
      setSelectedCustomerIds(prev => prev.filter(id => !pageIdsSet.has(id)));
    }
  };

  const handleToggleCustomerSelect = (id) => {
    setSelectedCustomerIds(prev =>
      prev.includes(id) ? prev.filter(cId => cId !== id) : [...prev, id]
    );
  };

  const handleSelectAllMatching = () => {
    const pageIds = sampleCustomers.map(c => c._id).filter(Boolean);
    setSelectedCustomerIds(prev => Array.from(new Set([...prev, ...pageIds])));
    toast.info(`Selected all ${sampleCustomers.length} visible customers on this page`);
  };

  const handleClearSelection = () => {
    setSelectedCustomerIds([]);
    toast.info('Cleared customer selection');
  };

  // Step Validation & Navigation
  const handleNext = () => {
    if (currentStep === 0 && !campaignName.trim()) {
      toast.error('Please enter a Campaign Name');
      return;
    }
    if (currentStep === 1 && !messageTemplate.trim() && !selectedTemplateId) {
      toast.error('Please select or write a message template');
      return;
    }
    if (currentStep === 2 && selectedCustomerIds.length === 0) {
      toast.error('Please select at least 1 customer for campaign audience');
      return;
    }
    setCurrentStep((prev) => Math.min(prev + 1, STEPS.length - 1));
  };

  const handlePrev = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 0));
  };

  const handleSelectTemplate = (e) => {
    const tId = e.target.value;
    setSelectedTemplateId(tId);
    const found = templates.find((t) => t._id === tId);
    if (found) {
      setMessageTemplate(found.bodyText || '');
      const hType = (found.headerType || found.mediaType || '').toUpperCase();
      const needsMedia = ['IMAGE', 'DOCUMENT', 'PDF', 'VIDEO'].includes(hType) || Boolean(found.headerUrl || found.mediaUrl);
      if (needsMedia) {
        setEnableMediaAttachment(true);
      }
    }
  };

  // Launch or Save Draft
  const handleLaunch = async (asDraft = false) => {
    if (!activeStoreId) {
      toast.error('Store ID is required to create a campaign');
      return;
    }

    if (!asDraft && selectedCustomerIds.length === 0) {
      toast.error('Please select at least 1 recipient customer');
      return;
    }

    setLaunching(true);
    try {
      const token = sessionStorage.getItem('Token') || localStorage.getItem('Token');
      const payload = {
        storeId: activeStoreId,
        campaignName,
        objective,
        campaignType,
        templateId: selectedTemplateId || null,
        messageTemplate,
        scheduleType: asDraft ? 'Draft' : scheduleType,
        status: asDraft ? 'Draft' : scheduleType === 'Immediate' ? 'In Progress' : 'Scheduled',
        scheduledAt: scheduleType === 'Scheduled' ? scheduledAt : null,
        audienceFilters: {
          search: searchQuery,
          membershipStatus,
          subscriberStatus,
          gender,
          city,
          state,
          minTotalSpend,
          maxTotalSpend,
          minLoyaltyPoints,
          birthdayMonth,
          anniversaryMonth,
          lastActiveDays,
          status: customerStatus,
          manualCustomerIds: selectedCustomerIds,
          selectedCustomerIds: selectedCustomerIds
        },
        manualCustomerIds: selectedCustomerIds,
        recipientCount: selectedCustomerIds.length,
        campaignAssets: uploadedAssets
      };

      let res;
      if (campaignId) {
        res = await postData(`campaigns/campaign/${campaignId}/update`, payload, token);
      } else {
        res = await postData('campaigns', payload, token);
      }

      if ((res?.status === 200 || res?.success || res?.data) && res?.data) {
        const cId = res.data._id || campaignId;
        toast.success(`Campaign ${asDraft ? 'saved as draft' : 'created successfully'}`);

        if (!asDraft && scheduleType === 'Immediate' && cId) {
          await postData(`campaigns/campaign/${cId}/execute`, {}, token);
          toast.success('Campaign execution dispatched');
        }

        navigate('/campaigns');
      } else {
        toast.error(res?.message || 'Failed to save campaign');
      }
    } catch (err) {
      console.error('Error launching campaign:', err);
      toast.error('Server error launching campaign');
    } finally {
      setLaunching(false);
    }
  };

  // Media Attachment Handlers
  const handleMediaFileChange = (type, file) => {
    if (!file) return;
    setMediaFiles(prev => ({ ...prev, [type]: file }));
  };

  const handleRemoveFile = (type) => {
    setMediaFiles(prev => ({ ...prev, [type]: null }));
    const assetType = type.charAt(0).toUpperCase() + type.slice(1);
    setUploadedAssets(prev => prev.filter(a => a.assetType !== assetType));
  };

  const handleUploadAllMedia = async () => {
    const token = sessionStorage.getItem('Token') || localStorage.getItem('Token');
    const assets = [];

    if (mediaLink.trim()) {
      assets.push({ assetType: 'Link', url: mediaLink.trim(), file: '', description: 'Campaign link' });
    }

    setMediaUploading(true);
    try {
      for (const type of ['image', 'pdf', 'video']) {
        const file = mediaFiles[type];
        if (!file) continue;
        const assetType = type.charAt(0).toUpperCase() + type.slice(1);
        try {
          const formData = new FormData();
          formData.append('file', file);
          const uploadRes = await uploadFile('upload/media', formData, token);
          const url = uploadRes?.data?.url || uploadRes?.url || uploadRes?.data?.fileUrl || '';
          assets.push({ assetType, url, file: url || file.name, description: `${assetType} attachment` });
        } catch {
          assets.push({ assetType, url: '', file: file.name, description: `${assetType} attachment (local)` });
        }
      }
      setUploadedAssets(assets);
      toast.success(`${assets.length} attachment(s) saved!`);
    } catch (err) {
      console.error('Media upload error:', err);
      toast.error('Failed to save media attachments');
    } finally {
      setMediaUploading(false);
    }
  };

  return (
    <div className='product-layout'>
      <div className="wizard-wrapper">
        <div className="wizard-card">
          {/* Header */}
          <div className="wizard-header">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h1 style={{ fontSize: '20px', fontWeight: 800, margin: 0, color: '#0f172a' }}>
                  {campaignId ? 'Manage & Dispatch Campaign' : 'Create New Marketing Campaign'}
                </h1>
                <p style={{ margin: '2px 0 0 0', fontSize: '12px', color: '#64748b' }}>
                  Step-by-step audience targeting, template selection, and automated execution.
                </p>
              </div>
              <button
                style={{ padding: '7px 16px', borderRadius: '8px', border: '1px solid #cbd5e1', background: '#fff', cursor: 'pointer', fontWeight: 600, fontSize: '13px', color: '#475569' }}
                onClick={() => navigate('/campaigns')}
              >
                Exit Wizard
              </button>
            </div>

            {/* Stepper Bar */}
            <div className="wizard-steps-bar">
              {STEPS.map((step, idx) => (
                <div
                  key={step}
                  onClick={() => idx < currentStep && setCurrentStep(idx)}
                  className={`step-item ${idx === currentStep ? 'active' : ''} ${idx < currentStep ? 'completed' : ''}`}
                  style={{ cursor: idx < currentStep ? 'pointer' : 'default' }}
                >
                  <div className="step-circle">{idx < currentStep ? <Icon icon="mdi:check" /> : idx + 1}</div>
                  <div className="step-label">{step}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Body Steps */}
          <div className="wizard-body">
            {/* STEP 1: Campaign Details */}
            {currentStep === 0 && (
              <div>
                <h2 style={{ fontSize: '18px', fontWeight: 700, marginBottom: '16px', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Icon icon="mdi:information-outline" style={{ color: '#3C0008' }} /> Step 1: Campaign Basic Info
                </h2>

                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: '6px', color: '#334155' }}>
                    Campaign Name *
                  </label>
                  <input
                    type="text"
                    value={campaignName}
                    onChange={(e) => setCampaignName(e.target.value)}
                    placeholder="e.g., Independence Day Salon Special Promo"
                    style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '14px', boxSizing: 'border-box' }}
                  />
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: '6px', color: '#334155' }}>
                    Communication Channel
                  </label>
                  <select
                    value="WhatsApp"
                    disabled
                    style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '14px', background: '#f8fafc', fontWeight: 600, color: '#0f172a', boxSizing: 'border-box' }}
                  >
                    <option value="WhatsApp">WhatsApp Business API</option>
                  </select>
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: '6px', color: '#334155' }}>
                    Campaign Objective / Internal Notes
                  </label>
                  <textarea
                    rows={3}
                    value={objective}
                    onChange={(e) => setObjective(e.target.value)}
                    placeholder="Describe the primary target goal of this marketing campaign..."
                    style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '14px', boxSizing: 'border-box' }}
                  />
                </div>
              </div>
            )}

            {/* STEP 2: Template & Content */}
            {currentStep === 1 && (
              <div>
                <h2 style={{ fontSize: '18px', fontWeight: 700, marginBottom: '16px', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Icon icon="mdi:file-document-outline" style={{ color: '#3C0008' }} /> Step 2: Message & Meta Template Selection
                </h2>

                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: '6px', color: '#334155' }}>
                    Select Reusable Meta Template (Optional)
                  </label>
                  <select
                    value={selectedTemplateId}
                    onChange={handleSelectTemplate}
                    style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '14px', boxSizing: 'border-box' }}
                  >
                    <option value="">-- Custom Message (No Template) --</option>
                    {templates.map((t) => {
                      const statusStr = (t.metaStatus || 'DRAFT').toUpperCase();
                      const isApproved = statusStr === 'APPROVED';
                      const formatStr = (t.headerType || t.mediaType || 'TEXT').toUpperCase();
                      return (
                        <option key={t._id} value={t._id}>
                          {isApproved ? '✓ [APPROVED] ' : `[${statusStr}] `} {t.templateName} ({t.category} - {formatStr})
                        </option>
                      );
                    })}
                  </select>
                </div>

                {selectedTemplateId && (() => {
                  const selectedT = templates.find(t => t._id === selectedTemplateId);
                  const statusStr = (selectedT?.metaStatus || 'DRAFT').toUpperCase();
                  if (statusStr !== 'APPROVED') {
                    return (
                      <div style={{ padding: '12px 16px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '10px', color: '#991b1b', fontSize: '13px', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <Icon icon="mdi:alert-circle" style={{ fontSize: '20px', flexShrink: 0 }} />
                        <span>Warning: Selected template status is <strong>{statusStr}</strong>. Only <strong>APPROVED</strong> templates can be dispatched to customers. Please submit to Meta in Template Library.</span>
                      </div>
                    );
                  }
                  return (
                    <div style={{ padding: '10px 14px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '10px', color: '#166534', fontSize: '13px', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Icon icon="mdi:check-circle" style={{ fontSize: '18px' }} />
                      <span>Meta Template Approved and Ready for Dispatch! {selectedHeaderType !== 'NONE' && selectedHeaderType !== 'TEXT' ? `(${selectedHeaderType} Header Required)` : ''}</span>
                    </div>
                  );
                })()}

                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: '6px', color: '#334155' }}>
                    Message Content *
                  </label>
                  <textarea
                    rows={6}
                    value={messageTemplate}
                    onChange={(e) => setMessageTemplate(e.target.value)}
                    placeholder="Hello {{CustomerName}}, special salon offers at {{StoreName}}!..."
                    style={{ width: '100%', padding: '12px 14px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '14px', fontFamily: 'inherit', boxSizing: 'border-box' }}
                  />
                </div>

                {/* Media Attachment Toggle if not auto-enabled */}
                {!isTemplateMediaRequired && (
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', fontSize: '13px', fontWeight: 600, color: '#334155', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={enableMediaAttachment}
                        onChange={(e) => setEnableMediaAttachment(e.target.checked)}
                        style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                      />
                      <span>Attach Image or PDF Document to Message</span>
                    </label>
                  </div>
                )}

                {/* Media Attachments Section (Rendered when template requires media or when attachment toggle is checked) */}
                {shouldDisplayMediaSection && (
                  <div style={{ background: '#f8fafc', borderRadius: '12px', padding: '20px', border: '1px solid #e2e8f0', marginBottom: '16px' }}>
                    <h3 style={{ fontSize: '15px', fontWeight: 700, color: '#1e293b', display: 'flex', alignItems: 'center', gap: '8px', margin: '0 0 16px 0' }}>
                      <Icon icon="mdi:paperclip" style={{ fontSize: '18px', color: '#3C0008' }} />
                      Header Media Attachment ({selectedHeaderType !== 'NONE' && selectedHeaderType !== 'TEXT' ? selectedHeaderType : 'Image / PDF'})
                    </h3>

                    <div style={{ marginBottom: '14px' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '13px', fontWeight: 600, marginBottom: '6px', color: '#475569' }}>
                        <Icon icon="mdi:link-variant" style={{ fontSize: '15px', color: '#3C0008' }} /> Media Link (URL)
                      </label>
                      <input
                        type="url"
                        value={mediaLink}
                        onChange={(e) => setMediaLink(e.target.value)}
                        placeholder="https://example.com/media-file.jpg"
                        style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '14px', boxSizing: 'border-box' }}
                      />
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px', marginBottom: '16px' }}>
                      {/* Image Card */}
                      <div
                        style={{ border: `2px dashed ${mediaFiles.image ? '#16a34a' : '#cbd5e1'}`, borderRadius: '10px', padding: '16px 12px', textAlign: 'center', cursor: 'pointer', background: mediaFiles.image ? '#f0fdf4' : '#fff', position: 'relative', transition: 'all 0.2s' }}
                        onClick={() => imageInputRef.current?.click()}
                      >
                        <Icon icon={mediaFiles.image ? 'mdi:image-check' : 'mdi:image-plus'} style={{ fontSize: '32px', color: mediaFiles.image ? '#16a34a' : '#94a3b8', display: 'block', margin: '0 auto 6px' }} />
                        <div style={{ fontSize: '13px', fontWeight: 700, color: '#475569' }}>Image File</div>
                        <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '2px' }}>JPG, PNG, WEBP</div>
                        {mediaFiles.image && <div style={{ fontSize: '11px', color: '#16a34a', marginTop: '4px', wordBreak: 'break-all', fontWeight: 600 }}>{mediaFiles.image.name}</div>}
                        <input ref={imageInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={(e) => handleMediaFileChange('image', e.target.files[0])} />
                        {mediaFiles.image && (
                          <button onClick={(e) => { e.stopPropagation(); handleRemoveFile('image'); }}
                            style={{ position: 'absolute', top: '6px', right: '6px', background: '#fee2e2', border: 'none', borderRadius: '50%', width: '20px', height: '20px', cursor: 'pointer', color: '#dc2626', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}>
                            <Icon icon="mdi:close" style={{ fontSize: '13px' }} />
                          </button>
                        )}
                      </div>

                      {/* PDF Card */}
                      <div
                        style={{ border: `2px dashed ${mediaFiles.pdf ? '#ca8a04' : '#cbd5e1'}`, borderRadius: '10px', padding: '16px 12px', textAlign: 'center', cursor: 'pointer', background: mediaFiles.pdf ? '#fefce8' : '#fff', position: 'relative', transition: 'all 0.2s' }}
                        onClick={() => pdfInputRef.current?.click()}
                      >
                        <Icon icon={mediaFiles.pdf ? 'mdi:file-pdf-box' : 'mdi:file-pdf-outline'} style={{ fontSize: '32px', color: mediaFiles.pdf ? '#ca8a04' : '#94a3b8', display: 'block', margin: '0 auto 6px' }} />
                        <div style={{ fontSize: '13px', fontWeight: 700, color: '#475569' }}>PDF Document</div>
                        <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '2px' }}>PDF documents only</div>
                        {mediaFiles.pdf && <div style={{ fontSize: '11px', color: '#ca8a04', marginTop: '4px', wordBreak: 'break-all', fontWeight: 600 }}>{mediaFiles.pdf.name}</div>}
                        <input ref={pdfInputRef} type="file" accept="application/pdf" style={{ display: 'none' }} onChange={(e) => handleMediaFileChange('pdf', e.target.files[0])} />
                        {mediaFiles.pdf && (
                          <button onClick={(e) => { e.stopPropagation(); handleRemoveFile('pdf'); }}
                            style={{ position: 'absolute', top: '6px', right: '6px', background: '#fee2e2', border: 'none', borderRadius: '50%', width: '20px', height: '20px', cursor: 'pointer', color: '#dc2626', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}>
                            <Icon icon="mdi:close" style={{ fontSize: '13px' }} />
                          </button>
                        )}
                      </div>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
                      <button
                        onClick={handleUploadAllMedia}
                        disabled={mediaUploading}
                        style={{ padding: '9px 20px', borderRadius: '8px', border: 'none', background: '#3C0008', color: '#fff', fontWeight: 700, cursor: mediaUploading ? 'not-allowed' : 'pointer', opacity: mediaUploading ? 0.7 : 1, display: 'flex', alignItems: 'center', gap: '7px', fontSize: '13.5px' }}
                      >
                        <Icon icon={mediaUploading ? 'mdi:loading' : 'mdi:cloud-upload-outline'} style={{ fontSize: '16px' }} />
                        {mediaUploading ? 'Saving...' : 'Save Media File'}
                      </button>
                      {uploadedAssets.length > 0 && (
                        <span style={{ fontSize: '13px', color: '#16a34a', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <Icon icon="mdi:check-circle" style={{ fontSize: '16px' }} />
                          {uploadedAssets.length} media file(s) attached
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* STEP 3: Audience Segmentation (Single Source of Truth) */}
            {currentStep === 2 && (
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '12px' }}>
                  <div>
                    <h2 style={{ fontSize: '18px', fontWeight: 700, color: '#1e293b', display: 'flex', alignItems: 'center', gap: '8px', margin: 0 }}>
                      <Icon icon="mdi:account-group-outline" style={{ color: '#3C0008' }} /> Step 3: Target Audience & Recipient Selection
                    </h2>
                    <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#64748b' }}>
                      Filter customers by demographics, spending, activity, or manually select recipients.
                    </p>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', padding: '6px 14px', borderRadius: '20px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Icon icon="mdi:check-circle" style={{ color: '#16a34a', fontSize: '18px' }} />
                      <span style={{ fontSize: '13px', fontWeight: 700, color: '#166534' }}>
                        {selectedCustomerIds.length} Selected
                      </span>
                      <span style={{ fontSize: '12px', color: '#15803d' }}>
                        (of {audienceTotalCount} Total)
                      </span>
                    </div>
                  </div>
                </div>

                {/* Audience Type Preset Cards */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px', marginBottom: '20px' }}>
                  {/* All Customers */}
                  <div
                    onClick={() => handleSelectAudienceType('ALL')}
                    style={{
                      padding: '14px 16px',
                      borderRadius: '12px',
                      border: audienceType === 'ALL' ? '2px solid #3C0008' : '1px solid #e2e8f0',
                      background: audienceType === 'ALL' ? '#fdf2f4' : '#ffffff',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                      <Icon icon="mdi:account-group" style={{ fontSize: '20px', color: audienceType === 'ALL' ? '#3C0008' : '#64748b' }} />
                      <span style={{ fontSize: '14px', fontWeight: 700, color: audienceType === 'ALL' ? '#3C0008' : '#1e293b' }}>All Customers</span>
                    </div>
                    <span style={{ fontSize: '11px', color: '#64748b' }}>Every customer in assigned store</span>
                  </div>

                  {/* Subscribers Only */}
                  <div
                    onClick={() => handleSelectAudienceType('MEMBERS')}
                    style={{
                      padding: '14px 16px',
                      borderRadius: '12px',
                      border: audienceType === 'MEMBERS' ? '2px solid #166534' : '1px solid #e2e8f0',
                      background: audienceType === 'MEMBERS' ? '#f0fdf4' : '#ffffff',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                      <Icon icon="mdi:card-membership" style={{ fontSize: '20px', color: audienceType === 'MEMBERS' ? '#166534' : '#64748b' }} />
                      <span style={{ fontSize: '14px', fontWeight: 700, color: audienceType === 'MEMBERS' ? '#166534' : '#1e293b' }}>Subscribers</span>
                    </div>
                    <span style={{ fontSize: '11px', color: '#64748b' }}>Active store members only</span>
                  </div>

                  {/* VIP Spenders */}
                  <div
                    onClick={() => handleSelectAudienceType('VIP')}
                    style={{
                      padding: '14px 16px',
                      borderRadius: '12px',
                      border: audienceType === 'VIP' ? '2px solid #ca8a04' : '1px solid #e2e8f0',
                      background: audienceType === 'VIP' ? '#fefce8' : '#ffffff',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                      <Icon icon="mdi:crown" style={{ fontSize: '20px', color: audienceType === 'VIP' ? '#ca8a04' : '#64748b' }} />
                      <span style={{ fontSize: '14px', fontWeight: 700, color: audienceType === 'VIP' ? '#ca8a04' : '#1e293b' }}>VIP Spenders</span>
                    </div>
                    <span style={{ fontSize: '11px', color: '#64748b' }}>Total Spend ≥ ₹5,000</span>
                  </div>

                  {/* Custom Filters Option */}
                  <div
                    onClick={() => setAudienceType('CUSTOM')}
                    style={{
                      padding: '14px 16px',
                      borderRadius: '12px',
                      border: audienceType === 'CUSTOM' ? '2px solid #0284c7' : '1px solid #e2e8f0',
                      background: audienceType === 'CUSTOM' ? '#f0f9ff' : '#ffffff',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                      <Icon icon="mdi:tune-vertical" style={{ fontSize: '20px', color: audienceType === 'CUSTOM' ? '#0284c7' : '#64748b' }} />
                      <span style={{ fontSize: '14px', fontWeight: 700, color: audienceType === 'CUSTOM' ? '#0284c7' : '#1e293b' }}>Custom Filters</span>
                    </div>
                    <span style={{ fontSize: '11px', color: '#64748b' }}>Demographics, spending & dates</span>
                  </div>
                </div>

                {/* Custom Filter Criteria Panel (Rendered when Custom Audience is active) */}
                {audienceType === 'CUSTOM' && (
                  <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '20px', marginBottom: '20px', boxShadow: '0 4px 16px rgba(0,0,0,0.03)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px', paddingBottom: '12px', borderBottom: '1px solid #f1f5f9' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <Icon icon="mdi:filter-variant" style={{ fontSize: '22px', color: '#3C0008' }} />
                        <div>
                          <h4 style={{ margin: 0, fontSize: '15px', fontWeight: 700, color: '#0f172a' }}>Custom Filter Panel</h4>
                          <p style={{ margin: '2px 0 0 0', fontSize: '12px', color: '#64748b' }}>Filter customers by name, phone, demographics, spending, and activity.</p>
                        </div>
                      </div>

                      <button
                        onClick={handleResetFilters}
                        style={{ padding: '6px 14px', borderRadius: '6px', border: '1px solid #cbd5e1', background: '#fff', color: '#64748b', fontSize: '12px', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}
                      >
                        <Icon icon="mdi:refresh" /> Reset Filters
                      </button>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: '14px' }}>
                      {/* Search */}
                      <div>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '12px', fontWeight: 600, marginBottom: '4px', color: '#475569' }}>
                          <Icon icon="mdi:magnify" /> Search Name / Phone
                        </label>
                        <input
                          type="text"
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          placeholder="Name, Phone, or Email"
                          style={{ width: '100%', padding: '8px 10px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '13px', boxSizing: 'border-box' }}
                        />
                      </div>

                      {/* Membership Status */}
                      <div>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '12px', fontWeight: 600, marginBottom: '4px', color: '#475569' }}>
                          <Icon icon="mdi:card-account-details-outline" /> Membership Status
                        </label>
                        <select
                          value={membershipStatus}
                          onChange={(e) => setMembershipStatus(e.target.value)}
                          style={{ width: '100%', padding: '8px 10px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '13px', boxSizing: 'border-box' }}
                        >
                          <option value="ALL">All Membership Types</option>
                          <option value="Member">Members Only</option>
                          <option value="Non-Member">Non-Members Only</option>
                        </select>
                      </div>

                      {/* Gender */}
                      <div>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '12px', fontWeight: 600, marginBottom: '4px', color: '#475569' }}>
                          <Icon icon="mdi:gender-male-female" /> Gender
                        </label>
                        <select
                          value={gender}
                          onChange={(e) => setGender(e.target.value)}
                          style={{ width: '100%', padding: '8px 10px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '13px', boxSizing: 'border-box' }}
                        >
                          <option value="ALL">All Genders</option>
                          <option value="Male">Male</option>
                          <option value="Female">Female</option>
                          <option value="others">Other</option>
                        </select>
                      </div>

                      {/* City / State */}
                      <div>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '12px', fontWeight: 600, marginBottom: '4px', color: '#475569' }}>
                          <Icon icon="mdi:map-marker-outline" /> City / Location
                        </label>
                        <input
                          type="text"
                          value={city}
                          onChange={(e) => setCity(e.target.value)}
                          placeholder="City / Location"
                          style={{ width: '100%', padding: '8px 10px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '13px', boxSizing: 'border-box' }}
                        />
                      </div>

                      {/* Min Spending */}
                      <div>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '12px', fontWeight: 600, marginBottom: '4px', color: '#475569' }}>
                          <Icon icon="mdi:currency-inr" /> Min Total Spend (₹)
                        </label>
                        <input
                          type="number"
                          value={minTotalSpend}
                          onChange={(e) => setMinTotalSpend(e.target.value)}
                          placeholder="e.g. 500"
                          style={{ width: '100%', padding: '8px 10px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '13px', boxSizing: 'border-box' }}
                        />
                      </div>

                      {/* Max Spending */}
                      <div>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '12px', fontWeight: 600, marginBottom: '4px', color: '#475569' }}>
                          <Icon icon="mdi:currency-inr" /> Max Total Spend (₹)
                        </label>
                        <input
                          type="number"
                          value={maxTotalSpend}
                          onChange={(e) => setMaxTotalSpend(e.target.value)}
                          placeholder="e.g. 50000"
                          style={{ width: '100%', padding: '8px 10px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '13px', boxSizing: 'border-box' }}
                        />
                      </div>

                      {/* Last Active (Days) */}
                      <div>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '12px', fontWeight: 600, marginBottom: '4px', color: '#475569' }}>
                          <Icon icon="mdi:clock-outline" /> Last Active (Days)
                        </label>
                        <input
                          type="number"
                          value={lastActiveDays}
                          onChange={(e) => setLastActiveDays(e.target.value)}
                          placeholder="e.g. 30"
                          style={{ width: '100%', padding: '8px 10px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '13px', boxSizing: 'border-box' }}
                        />
                      </div>

                      {/* Birthday Month */}
                      <div>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '12px', fontWeight: 600, marginBottom: '4px', color: '#475569' }}>
                          <Icon icon="mdi:cake-variant-outline" /> Birthday Month
                        </label>
                        <select
                          value={birthdayMonth}
                          onChange={(e) => setBirthdayMonth(e.target.value)}
                          style={{ width: '100%', padding: '8px 10px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '13px', boxSizing: 'border-box' }}
                        >
                          <option value="">Any Birthday Month</option>
                          {['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'].map((m, idx) => (
                            <option key={m} value={idx + 1}>{m} (Month {idx + 1})</option>
                          ))}
                        </select>
                      </div>

                      {/* Anniversary Month */}
                      <div>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '12px', fontWeight: 600, marginBottom: '4px', color: '#475569' }}>
                          <Icon icon="mdi:heart-outline" /> Anniversary Month
                        </label>
                        <select
                          value={anniversaryMonth}
                          onChange={(e) => setAnniversaryMonth(e.target.value)}
                          style={{ width: '100%', padding: '8px 10px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '13px', boxSizing: 'border-box' }}
                        >
                          <option value="">Any Anniversary Month</option>
                          {['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'].map((m, idx) => (
                            <option key={m} value={idx + 1}>{m} (Month {idx + 1})</option>
                          ))}
                        </select>
                      </div>

                      {/* Customer Status */}
                      <div>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '12px', fontWeight: 600, marginBottom: '4px', color: '#475569' }}>
                          <Icon icon="mdi:account-check-outline" /> Active Status
                        </label>
                        <select
                          value={customerStatus}
                          onChange={(e) => setCustomerStatus(e.target.value)}
                          style={{ width: '100%', padding: '8px 10px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '13px', boxSizing: 'border-box' }}
                        >
                          <option value="ALL">All Customers</option>
                          <option value="Active">Active Customers</option>
                          <option value="Inactive">Inactive Customers</option>
                        </select>
                      </div>
                    </div>
                  </div>
                )}

                {/* Audience Table Bar & Batch Actions */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px 10px 0 0', flexWrap: 'wrap', gap: '10px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <span style={{ fontSize: '13px', fontWeight: 700, color: '#1e293b' }}>
                      Matching Audience List
                    </span>
                    <span style={{ fontSize: '12px', color: '#64748b', background: '#e2e8f0', padding: '2px 8px', borderRadius: '12px', fontWeight: 600 }}>
                      Page {audiencePage} of {audienceTotalPages}
                    </span>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <button
                      type="button"
                      onClick={handleSelectAllMatching}
                      style={{ padding: '6px 12px', borderRadius: '6px', border: '1px solid #166534', background: '#166534', color: '#fff', fontSize: '12px', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px' }}
                    >
                      <Icon icon="mdi:checkbox-marked-circle-outline" /> Select Page ({sampleCustomers.length})
                    </button>

                    {selectedCustomerIds.length > 0 && (
                      <button
                        type="button"
                        onClick={handleClearSelection}
                        style={{ padding: '6px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', background: '#fff', color: '#64748b', fontSize: '12px', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px' }}
                      >
                        <Icon icon="mdi:close-circle-outline" /> Clear Selection ({selectedCustomerIds.length})
                      </button>
                    )}

                    <button
                      type="button"
                      onClick={() => handlePreviewAudience(false, audiencePage)}
                      style={{ padding: '6px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', background: '#fff', color: '#334155', fontSize: '12px', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px' }}
                    >
                      <Icon icon="mdi:refresh" /> Refresh
                    </button>
                  </div>
                </div>

                {/* Recipient Customer Preview Table */}
                {previewLoading ? (
                  <div style={{ padding: '40px', textAlign: 'center', background: '#ffffff', border: '1px solid #e2e8f0', borderTop: 'none', borderRadius: '0 0 10px 10px' }}>
                    <Icon icon="mdi:loading" style={{ fontSize: '32px', color: '#3C0008' }} />
                    <div style={{ fontSize: '14px', fontWeight: 600, color: '#475569', marginTop: '10px' }}>Filtering target customers...</div>
                  </div>
                ) : sampleCustomers.length === 0 ? (
                  <div style={{ padding: '40px 20px', textAlign: 'center', background: '#ffffff', border: '1px solid #e2e8f0', borderTop: 'none', borderRadius: '0 0 10px 10px' }}>
                    <Icon icon="mdi:account-search-outline" style={{ fontSize: '44px', color: '#94a3b8', marginBottom: '8px' }} />
                    <h4 style={{ margin: '0 0 4px 0', fontSize: '15px', fontWeight: 700, color: '#334155' }}>No Matching Customers Found</h4>
                    <p style={{ margin: 0, fontSize: '13px', color: '#64748b' }}>Try adjusting your filter criteria or clearing search query.</p>
                  </div>
                ) : (
                  <div style={{ border: '1px solid #e2e8f0', borderTop: 'none', borderRadius: '0 0 10px 10px', overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                      <thead>
                        <tr style={{ background: '#3C0008', borderBottom: '1px solid #e2e8f0', textAlign: 'left' }}>
                          <th style={{ padding: '10px 12px', textAlign: 'center', width: '80px', color: '#ffffff' }}>
                            <input
                              type="checkbox"
                              checked={isPageAllSelected}
                              ref={el => el && (el.indeterminate = isPageSomeSelected && !isPageAllSelected)}
                              onChange={handleToggleSelectAllPage}
                              style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                              title="Select / Deselect all on current page"
                            />
                          </th>
                          <th style={{ padding: '10px 12px', fontWeight: 700, color: '#ffffff' }}>Customer Name</th>
                          <th style={{ padding: '10px 12px', fontWeight: 700, color: '#ffffff' }}>Contact Number</th>
                          <th style={{ padding: '10px 12px', fontWeight: 700, color: '#ffffff' }}>City / Location</th>
                          <th style={{ padding: '10px 12px', fontWeight: 700, color: '#ffffff' }}>Membership</th>
                          <th style={{ padding: '10px 12px', fontWeight: 700, color: '#ffffff' }}>Total Spend</th>
                          <th style={{ padding: '10px 12px', fontWeight: 700, color: '#ffffff', textAlign: 'center' }}>Select</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sampleCustomers.map((cust) => {
                          const isSelected = selectedCustomerIds.includes(cust._id);
                          const custName = cust.name || cust.customerName || `${cust.firstName || ''} ${cust.lastName || ''}`.trim() || 'Customer';
                          const custContact = cust.phoneNumber || cust.email || '-';
                          const isMember = (cust.subscriptionIds && cust.subscriptionIds.length > 0) || cust.membershipStatus === 'Member';
                          return (
                            <tr
                              key={cust._id}
                              onClick={() => handleToggleCustomerSelect(cust._id)}
                              style={{
                                borderBottom: '1px solid #f1f5f9',
                                background: isSelected ? '#f0fdf4' : '#ffffff',
                                cursor: 'pointer',
                                transition: 'background-color 0.15s ease'
                              }}
                            >
                              <td style={{ padding: '8px 12px', textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={() => handleToggleCustomerSelect(cust._id)}
                                  style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                                />
                              </td>
                              <td style={{ padding: '8px 12px', fontWeight: isSelected ? 700 : 500, color: '#0f172a' }}>
                                {custName}
                              </td>
                              <td style={{ padding: '8px 12px', color: '#475569' }}>{custContact}</td>
                              <td style={{ padding: '8px 12px', color: '#64748b' }}>{cust.city || cust.state || '-'}</td>
                              <td style={{ padding: '8px 12px' }}>
                                <span style={{ fontSize: '11px', fontWeight: 600, padding: '2px 8px', borderRadius: '10px', background: isMember ? '#dcfce7' : '#f1f5f9', color: isMember ? '#15803d' : '#64748b' }}>
                                  {isMember ? 'Member' : 'Standard'}
                                </span>
                              </td>
                              <td style={{ padding: '8px 12px', fontWeight: 600, color: '#0f172a' }}>
                                ₹{(cust.totalSpend || 0).toFixed(2)}
                              </td>
                              <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                                <span style={{ fontSize: '11px', fontWeight: 600, padding: '2px 8px', borderRadius: '4px', background: isSelected ? '#166534' : '#f1f5f9', color: isSelected ? '#ffffff' : '#64748b' }}>
                                  {isSelected ? 'Selected' : 'Select'}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Pagination Controls */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '14px', flexWrap: 'wrap', gap: '10px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#64748b' }}>
                    <span>Showing page <strong>{audiencePage}</strong> of <strong>{audienceTotalPages}</strong></span>
                    <span>({audienceTotalCount} total records)</span>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <label style={{ fontSize: '12px', color: '#64748b', fontWeight: 600 }}>Rows per page:</label>
                    <select
                      value={audienceLimit}
                      onChange={(e) => {
                        setAudienceLimit(Number(e.target.value));
                        setAudiencePage(1);
                      }}
                      style={{ padding: '4px 8px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '12px' }}
                    >
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>

                    <button
                      disabled={audiencePage <= 1}
                      onClick={() => setAudiencePage(prev => Math.max(1, prev - 1))}
                      style={{ padding: '5px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', background: audiencePage <= 1 ? '#f1f5f9' : '#fff', cursor: audiencePage <= 1 ? 'not-allowed' : 'pointer', fontSize: '12px', fontWeight: 600 }}
                    >
                      Previous
                    </button>

                    <button
                      disabled={audiencePage >= audienceTotalPages}
                      onClick={() => setAudiencePage(prev => Math.min(audienceTotalPages, prev + 1))}
                      style={{ padding: '5px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', background: audiencePage >= audienceTotalPages ? '#f1f5f9' : '#fff', cursor: audiencePage >= audienceTotalPages ? 'not-allowed' : 'pointer', fontSize: '12px', fontWeight: 600 }}
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* STEP 4: Review & Schedule / Launch */}
            {currentStep === 3 && (
              <div>
                <h2 style={{ fontSize: '18px', fontWeight: 700, marginBottom: '16px', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Icon icon="mdi:clipboard-check-outline" style={{ color: '#3C0008' }} /> Step 4: Campaign Review & Launch
                </h2>

                <div style={{ background: '#f8fafc', padding: '18px', borderRadius: '12px', border: '1px solid #e2e8f0', marginBottom: '20px' }}>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: 700, marginBottom: '8px', color: '#1e293b' }}>
                    Dispatch Schedule & Timing
                  </label>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
                    <div>
                      <select
                        value={scheduleType}
                        onChange={(e) => setScheduleType(e.target.value)}
                        style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '14px', boxSizing: 'border-box' }}
                      >
                        <option value="Immediate">Send Immediately</option>
                        <option value="Scheduled">Schedule for Later</option>
                      </select>
                    </div>

                    {scheduleType === 'Scheduled' && (
                      <div>
                        <input
                          type="datetime-local"
                          value={scheduledAt}
                          onChange={(e) => setScheduledAt(e.target.value)}
                          style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '14px', boxSizing: 'border-box' }}
                        />
                      </div>
                    )}
                  </div>
                </div>

                {/* Final Review Summary Cards */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px', marginBottom: '20px' }}>
                  {/* Campaign Summary Card */}
                  <div style={{ border: '1px solid #cbd5e1', borderRadius: '12px', padding: '18px', background: '#ffffff' }}>
                    <h4 style={{ margin: '0 0 12px 0', fontSize: '15px', fontWeight: 700, color: '#3C0008', display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <Icon icon="mdi:bullhorn-outline" /> Campaign Overview
                    </h4>
                    <p style={{ margin: '6px 0', fontSize: '13.5px', color: '#334155' }}>
                      <strong>Name:</strong> {campaignName || 'Untitled Campaign'}
                    </p>
                    <p style={{ margin: '6px 0', fontSize: '13.5px', color: '#334155' }}>
                      <strong>Channel:</strong> {campaignType}
                    </p>
                    <p style={{ margin: '6px 0', fontSize: '13.5px', color: '#334155' }}>
                      <strong>Schedule Mode:</strong> {scheduleType}
                    </p>
                  </div>

                  {/* Audience Summary Card */}
                  <div style={{ border: '1px solid #bbf7d0', borderRadius: '12px', padding: '18px', background: '#f0fdf4' }}>
                    <h4 style={{ margin: '0 0 12px 0', fontSize: '15px', fontWeight: 700, color: '#166534', display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <Icon icon="mdi:account-check" /> Targeted Audience
                    </h4>
                    <p style={{ margin: '6px 0', fontSize: '18px', fontWeight: 800, color: '#166534' }}>
                      {selectedCustomerIds.length} Recipients
                    </p>
                    <p style={{ margin: '4px 0', fontSize: '12px', color: '#15803d' }}>
                      Exact persistent recipient selection across steps.
                    </p>
                    <span style={{ fontSize: '11px', background: '#dcfce7', color: '#166534', fontWeight: 600, padding: '3px 10px', borderRadius: '12px', display: 'inline-block', marginTop: '6px' }}>
                      Preset: {audienceType} Audience
                    </span>
                  </div>

                  {/* Message & Template Card */}
                  <div style={{ border: '1px solid #cbd5e1', borderRadius: '12px', padding: '18px', background: '#ffffff' }}>
                    <h4 style={{ margin: '0 0 12px 0', fontSize: '15px', fontWeight: 700, color: '#0f172a', display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <Icon icon="mdi:message-text-outline" /> Content & Attachments
                    </h4>
                    <p style={{ margin: '6px 0', fontSize: '13px', color: '#475569', maxHeight: '60px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      "{messageTemplate || 'No message template text specified.'}"
                    </p>
                    <p style={{ margin: '6px 0 0 0', fontSize: '12px', color: '#64748b' }}>
                      Attachments: <strong>{uploadedAssets.length} Ready</strong>
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Footer Navigation Buttons */}
          <div className="wizard-footer">
            <button
              style={{ padding: '9px 20px', borderRadius: '8px', border: '1px solid #cbd5e1', background: '#fff', cursor: currentStep === 0 ? 'not-allowed' : 'pointer', fontWeight: 600, color: '#475569', opacity: currentStep === 0 ? 0.5 : 1 }}
              onClick={handlePrev}
              disabled={currentStep === 0}
            >
              Previous Step
            </button>

            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                style={{ padding: '9px 20px', borderRadius: '8px', border: '1px solid #cbd5e1', background: '#fff', cursor: 'pointer', fontWeight: 600, color: '#334155' }}
                onClick={() => handleLaunch(true)}
                disabled={launching}
              >
                Save as Draft
              </button>

              {currentStep < STEPS.length - 1 ? (
                <button
                  style={{ padding: '9px 24px', borderRadius: '8px', border: 'none', background: '#3C0008', color: '#fff', cursor: 'pointer', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '6px' }}
                  onClick={handleNext}
                >
                  <span>Next Step</span>
                  <Icon icon="mdi:arrow-right" />
                </button>
              ) : (
                <button
                  style={{ padding: '9px 28px', borderRadius: '8px', border: 'none', background: '#16a34a', color: '#fff', cursor: launching ? 'not-allowed' : 'pointer', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '8px', boxShadow: '0 4px 12px rgba(22, 163, 74, 0.3)' }}
                  onClick={() => handleLaunch(false)}
                  disabled={launching}
                >
                  <Icon icon={launching ? 'mdi:loading' : 'mdi:rocket-launch'} style={{ fontSize: '18px' }} />
                  <span>{launching ? 'Dispatching Campaign...' : 'Launch & Dispatch Campaign'}</span>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default CampaignWizard;
